import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import {
  useTableDetail,
  useUpdateTableReview,
  useUpdateColumnReview,
} from '../../hooks/useDbDictionary.js';
import { useAuth } from '../../hooks/useAuth.js';
import { DescriptionEditor } from '../../components/features/db-dictionary/DescriptionEditor.js';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DB辞書テーブル詳細ページ。カラム定義・AI説明・人間レビュー編集・isDead/checked管理
 * @graph-connects useTableDetail [calls] テーブル詳細フック呼び出し
 * @graph-connects useUpdateTableReview [calls] テーブルレビュー更新フック呼び出し
 * @graph-connects useUpdateColumnReview [calls] カラムレビュー更新フック呼び出し
 * @graph-connects DescriptionEditor [calls] 説明文エディターコンポーネント呼び出し
 */
export const Route = createFileRoute('/db-dictionary/$schema/$table')({
  component: DbDictionaryTableDetailPage,
});

/**
 * テーブル詳細ページ
 */
/** @graph-connects none */
function DbDictionaryTableDetailPage() {
  const { t } = useTranslation();
  const { schema, table } = Route.useParams();
  const { can } = useAuth();
  const canEdit = can('EDIT_DB_DICTIONARY');
  const { data, isLoading, isError, refetch } = useTableDetail(schema, table);
  const updateTableReview = useUpdateTableReview();
  const updateColumnReview = useUpdateColumnReview();

  // テーブルレベルの編集状態
  const [editingTableDesc, setEditingTableDesc] = useState(false);
  const [tableDescValue, setTableDescValue] = useState('');

  // カラムレベルの編集状態（カラム名をキーにした編集中の値）
  const [editingColumnDesc, setEditingColumnDesc] = useState<string | null>(null);
  const [columnDescValue, setColumnDescValue] = useState('');

  /** テーブルを確認済みにする */
  const handleMarkAsChecked = () => {
    updateTableReview.mutate({
      schema,
      table,
      data: { isChecked: true },
    });
  };

  /** テーブルの廃止状態を切り替える */
  const handleToggleDead = () => {
    if (!data) return;
    updateTableReview.mutate({
      schema,
      table,
      data: { isDead: !data.table.isDead },
    });
  };

  /** テーブルの説明を保存する */
  const handleSaveTableDescription = () => {
    updateTableReview.mutate(
      {
        schema,
        table,
        data: { humanDescription: tableDescValue },
      },
      {
        onSuccess: () => {
          setEditingTableDesc(false);
        },
      },
    );
  };

  /** カラムの説明を保存する */
  const handleSaveColumnDescription = (columnName: string) => {
    updateColumnReview.mutate(
      {
        schema,
        table,
        column: columnName,
        data: { humanDescription: columnDescValue },
      },
      {
        onSuccess: () => {
          setEditingColumnDesc(null);
        },
      },
    );
  };

  /** カラムの廃止状態を切り替える */
  const handleToggleColumnDead = (columnName: string, currentIsDead: boolean) => {
    updateColumnReview.mutate({
      schema,
      table,
      column: columnName,
      data: { isDead: !currentIsDead },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <div className="h-4 w-32 rounded bg-[var(--color-bg-elevated)] animate-pulse mb-4" />
          <div className="h-7 w-48 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
          <div className="h-4 w-24 mt-2 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
        </div>
        {/* テーブル説明スケルトン */}
        <Card>
          <CardHeader title="" />
          <CardContent>
            <div className="h-3 w-20 rounded bg-[var(--color-bg-elevated)] animate-pulse mb-2" />
            <div className="h-20 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse mb-4" />
            <div className="h-3 w-24 rounded bg-[var(--color-bg-elevated)] animate-pulse mb-2" />
            <div className="h-16 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
          </CardContent>
        </Card>
        {/* カラム一覧スケルトン */}
        <Card>
          <CardHeader title="" />
          <CardContent>
            <div className="hidden lg:block">
              <div className="border-b border-[var(--color-border)] py-3 flex gap-4 px-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-4 w-20 rounded bg-[var(--color-bg-elevated)] animate-pulse"
                  />
                ))}
              </div>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="border-b border-[var(--color-border)] py-3 flex gap-4 px-4">
                  <div className="h-4 w-28 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  <div className="h-4 w-20 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  <div className="h-4 w-8 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  <div className="h-4 w-64 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  <div className="h-4 w-10 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                </div>
              ))}
            </div>
            <div className="block lg:hidden space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
                >
                  <div className="h-4 w-32 rounded bg-[var(--color-bg-base)] animate-pulse mb-3" />
                  <div className="h-3 w-full rounded bg-[var(--color-bg-base)] animate-pulse mb-2" />
                  <div className="h-3 w-2/3 rounded bg-[var(--color-bg-base)] animate-pulse" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{table}</h1>
        </div>
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  const { table: tableInfo, columns } = data;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ナビゲーションとヘッダー */}
      <div className="flex flex-col gap-4 animate-content-reveal">
        <Link
          to="/db-dictionary/$schema"
          params={{ schema }}
          className="inline-flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('dbDictionary.actions.backToTables')}
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
                {tableInfo.name}
              </h1>
              {tableInfo.isDead && <Badge variant="error">{t('dbDictionary.table.dead')}</Badge>}
              {tableInfo.isChecked && (
                <Badge variant="success">{t('dbDictionary.table.checked')}</Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{schema}</p>
          </div>
          {canEdit && (
            <div className="flex gap-2 flex-wrap">
              {!tableInfo.isChecked && (
                <Button
                  variant="primary"
                  size="sm"
                  loading={updateTableReview.isPending}
                  onClick={handleMarkAsChecked}
                >
                  {t('dbDictionary.table.markAsChecked')}
                </Button>
              )}
              <Button
                variant={tableInfo.isDead ? 'secondary' : 'danger'}
                size="sm"
                loading={updateTableReview.isPending}
                onClick={handleToggleDead}
              >
                {tableInfo.isDead
                  ? t('dbDictionary.table.alive')
                  : t('dbDictionary.table.markAsDead')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* テーブル説明セクション */}
      <Card className="animate-content-reveal">
        <CardHeader title={t('dbDictionary.table.description')} />
        <CardContent>
          {/* Gemini生成の説明（読み取り専用） */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="info" size="sm">
                {t('dbDictionary.table.generatedDescription')}
              </Badge>
            </div>
            <p className="text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap rounded-lg bg-[var(--color-bg-elevated)] p-3 border border-[var(--color-border)]">
              {tableInfo.description || '-'}
            </p>
          </div>

          {/* レビュー説明（編集可能） */}
          <DescriptionEditor
            label={t('dbDictionary.table.humanDescription')}
            value={editingTableDesc ? tableDescValue : (tableInfo.humanDescription ?? '')}
            isEditing={editingTableDesc}
            isSaving={updateTableReview.isPending}
            generatedText={tableInfo.description}
            readOnly={!canEdit}
            onEdit={() => {
              setTableDescValue(tableInfo.humanDescription ?? '');
              setEditingTableDesc(true);
            }}
            onChange={setTableDescValue}
            onSave={handleSaveTableDescription}
            onCancel={() => {
              setEditingTableDesc(false);
            }}
          />

          {/* 廃止メモ */}
          {tableInfo.isDead && tableInfo.deadNote && (
            <div className="mt-4">
              <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                {t('dbDictionary.table.deadNote')}
              </p>
              <p className="text-sm text-[var(--color-error)] whitespace-pre-wrap">
                {tableInfo.deadNote}
              </p>
            </div>
          )}

          {/* メタ情報 */}
          {tableInfo.isChecked && (
            <div className="mt-4 flex gap-6 text-xs text-[var(--color-text-muted)]">
              {tableInfo.checkedAt && (
                <span>
                  {t('dbDictionary.table.checkedAt')}: {tableInfo.checkedAt}
                </span>
              )}
              {tableInfo.checkedBy && (
                <span>
                  {t('dbDictionary.table.checkedBy')}: {tableInfo.checkedBy}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* カラム一覧 */}
      <Card className="animate-content-reveal">
        <CardHeader
          title={t('dbDictionary.stats.columnCount')}
          description={`${String(columns.length)} ${t('dbDictionary.table.columns').toLowerCase()}`}
        />
        <CardContent>
          {/* モバイル: カードビュー */}
          <div className="block lg:hidden space-y-3">
            {columns.map((col) => (
              <div
                key={col.qualifiedName}
                className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
              >
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="font-medium text-[var(--color-text-primary)] font-mono text-sm truncate">
                    {col.name}
                  </span>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Badge variant="default" size="sm">
                      {col.dataType}
                    </Badge>
                    {col.isDead && (
                      <Badge variant="error" size="sm">
                        {t('dbDictionary.column.dead')}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div>
                    <p className="text-[var(--color-text-muted)]">
                      {t('dbDictionary.column.nullable')}
                    </p>
                    <p className="text-[var(--color-text-primary)]">
                      {col.nullable ? t('dbDictionary.column.yes') : t('dbDictionary.column.no')}
                    </p>
                  </div>
                  {col.isDead && col.deadEmptyPct !== null && (
                    <div>
                      <p className="text-[var(--color-text-muted)]">
                        {t('dbDictionary.column.emptyPct')}
                      </p>
                      <p className="text-[var(--color-error)]">{col.deadEmptyPct}%</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-[var(--color-text-secondary)] mb-2">
                  {col.description || '-'}
                </p>

                {/* カラム説明の編集 */}
                <DescriptionEditor
                  label={t('dbDictionary.column.humanDescription')}
                  value={
                    editingColumnDesc === col.name ? columnDescValue : (col.humanDescription ?? '')
                  }
                  isEditing={editingColumnDesc === col.name}
                  isSaving={updateColumnReview.isPending}
                  generatedText={col.description}
                  compact
                  readOnly={!canEdit}
                  onEdit={() => {
                    setColumnDescValue(col.humanDescription ?? '');
                    setEditingColumnDesc(col.name);
                  }}
                  onChange={setColumnDescValue}
                  onSave={() => {
                    handleSaveColumnDescription(col.name);
                  }}
                  onCancel={() => {
                    setEditingColumnDesc(null);
                  }}
                />

                {/* 廃止切り替え */}
                {canEdit && (
                  <div className="mt-2">
                    <Button
                      variant={col.isDead ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        handleToggleColumnDead(col.name, col.isDead);
                      }}
                    >
                      {col.isDead ? t('dbDictionary.table.alive') : t('dbDictionary.column.dead')}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* デスクトップ: テーブルビュー */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('dbDictionary.column.name')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('dbDictionary.column.dataType')}
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('dbDictionary.column.nullable')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('dbDictionary.column.description')}
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('dbDictionary.column.dead')}
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('dbDictionary.column.emptyPct')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {columns.map((col) => (
                  <tr
                    key={col.qualifiedName}
                    className={`border-b border-[var(--color-border)] last:border-b-0 ${col.isDead ? 'opacity-60' : ''}`}
                  >
                    <td className="py-3 px-4">
                      <span className="text-sm font-medium font-mono text-[var(--color-text-primary)]">
                        {col.name}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="default" size="sm">
                        {col.dataType}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-[var(--color-text-secondary)]">
                      {col.nullable ? t('dbDictionary.column.yes') : t('dbDictionary.column.no')}
                    </td>
                    <td className="py-3 px-4 max-w-md">
                      {canEdit && editingColumnDesc === col.name ? (
                        <DescriptionEditor
                          label=""
                          value={columnDescValue}
                          isEditing
                          isSaving={updateColumnReview.isPending}
                          generatedText={col.description}
                          compact
                          onEdit={() => {
                            /* 既に編集中 */
                          }}
                          onChange={setColumnDescValue}
                          onSave={() => {
                            handleSaveColumnDescription(col.name);
                          }}
                          onCancel={() => {
                            setEditingColumnDesc(null);
                          }}
                        />
                      ) : (
                        <div
                          className={canEdit ? 'cursor-pointer group' : ''}
                          onClick={
                            canEdit
                              ? () => {
                                  setColumnDescValue(col.humanDescription ?? '');
                                  setEditingColumnDesc(col.name);
                                }
                              : undefined
                          }
                        >
                          <p className="text-sm text-[var(--color-text-secondary)]">
                            {col.description || '-'}
                          </p>
                          {col.humanDescription && col.humanDescription !== col.description && (
                            <p className="text-xs text-[var(--color-accent-primary)] mt-1">
                              {col.humanDescription}
                            </p>
                          )}
                          {canEdit && (
                            <span className="text-xs text-[var(--color-text-muted)] opacity-0 group-hover:opacity-100 transition-opacity">
                              {t('actions.edit')}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {canEdit ? (
                        <button
                          onClick={() => {
                            handleToggleColumnDead(col.name, col.isDead);
                          }}
                          className="inline-flex items-center justify-center"
                          aria-label={
                            col.isDead
                              ? t('dbDictionary.table.alive')
                              : t('dbDictionary.column.dead')
                          }
                        >
                          {col.isDead ? (
                            <Badge variant="error" size="sm">
                              {t('dbDictionary.column.dead')}
                            </Badge>
                          ) : (
                            <span className="w-5 h-5 block rounded-full border-2 border-[var(--color-border)] hover:border-[var(--color-error)] transition-colors" />
                          )}
                        </button>
                      ) : (
                        col.isDead && (
                          <Badge variant="error" size="sm">
                            {t('dbDictionary.column.dead')}
                          </Badge>
                        )
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-sm">
                      {col.isDead && col.deadEmptyPct !== null ? (
                        <span className="text-[var(--color-error)]">{col.deadEmptyPct}%</span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
